import { Link } from 'react-router-dom';

interface ContentLink {
  label: string;
  to: string;
}

interface ContentSection {
  id: string;
  title: string;
  body: React.ReactNode;
}

interface CalculatorContentWrapperProps {
  eyebrow?: string;
  title: string;
  intro: React.ReactNode;
  sections: ContentSection[];
  methodologyNote?: React.ReactNode;
  relatedLinks?: ContentLink[];
}

export function CalculatorContentWrapper({
  eyebrow,
  title,
  intro,
  sections,
  methodologyNote,
  relatedLinks = [],
}: CalculatorContentWrapperProps) {
  return (
    <section className="mt-12 pt-8" style={{ borderTop: '1px solid var(--outline-variant)' }}>
      <div className="max-w-[880px]">
        {eyebrow && (
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2 text-[#0078d4]">
            {eyebrow}
          </p>
        )}
        <h2 className="text-[24px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h2>
        <div className="text-[15px] leading-relaxed mb-8" style={{ color: 'var(--text-secondary)' }}>
          {intro}
        </div>

        <div className="flex flex-col gap-6">
          {sections.map((section) => (
            <article
              key={section.id}
              id={section.id}
              className="rounded-lg p-5"
              style={{
                backgroundColor: 'var(--surface-container)',
                border: '1px solid var(--outline-variant)',
              }}
            >
              <h3 className="text-[18px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                {section.title}
              </h3>
              <div className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {section.body}
              </div>
            </article>
          ))}
        </div>

        {methodologyNote && (
          <div
            className="mt-6 rounded-lg p-4"
            style={{
              backgroundColor: 'var(--surface-container-low)',
              border: '1px solid rgba(0, 120, 212, 0.3)',
            }}
          >
            <p className="text-[11px] font-bold uppercase tracking-widest mb-2 text-[#0078d4]">
              Methodology
            </p>
            <div className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {methodologyNote}
            </div>
          </div>
        )}

        {relatedLinks.length > 0 && (
          <div className="mt-6">
            <h3 className="text-[13px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Related Pages
            </h3>
            <div className="flex flex-wrap gap-3">
              {relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded transition-colors"
                  style={{
                    backgroundColor: 'var(--surface-container)',
                    border: '1px solid var(--outline-variant)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span className="material-symbols-outlined text-[#0078d4] text-[16px]">
                    arrow_forward
                  </span>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
